import { Injectable, Injector } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { PagingResponse } from '@models/paging-response.model';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { ODataRequest } from '../utilities/sdk-base/o-data-request';
import { Collection } from '../models/ui/collection.model';
import { Issue } from '../models/ui/issue.model';
import { IssueResource } from '../models/resource/issue.resource';
import { IssueResourceToIssueMapper } from '../models/mappers/issue-resource-to-issue.mapper';
import { IssueToIssueResourceMapper } from '../models/mappers/issue-to-issue-resource.mapper';

@Injectable({
	providedIn: 'root'
})
export class IssueSdk extends SdkBase {

	constructor(injector: Injector) {
		super(injector);
	}

	public get(params?: GetParams): Observable<Collection<Issue>> {
		const request = new ODataRequest('get', 'issues');
		if (params?.id) {
			request.$filter<IssueResource>([{
				column: 'issueId',
				value: params?.id,
				operation: 'equals'
			}]);
		} else if (params?.searchTerm) {
			request.requestParams.append('$search', params.searchTerm);
		}

		return this.makeODataRequest$<PagingResponse<IssueResource>>(request, 'api2').pipe(
			map((response) => {
				const mapper = new IssueResourceToIssueMapper();
				return new Collection<Issue>({
					items: mapper.mapCollection(response.value),
					totalItems: response.count
				});
			})
		);
	}

	public post(params: PostParams): Observable<Issue> {
		if (params?.issue) {
			const issueToIssueResourceMapper = new IssueToIssueResourceMapper();
			const body = issueToIssueResourceMapper.map(params.issue);
			const request = new ODataRequest('post', 'issues', body);
			return this.makeODataRequest$<IssueResource>(request, 'api2').pipe(
				map((response) => {
					const mapper = new IssueResourceToIssueMapper();
					return mapper.map(response);
				})
			);
		}

		return of(null);
	}

	public put(params: PutParams): Observable<Issue> {
		if (params?.issue) {
			const issueToIssueResourceMapper = new IssueToIssueResourceMapper();
			const body = issueToIssueResourceMapper.map(params.issue);
			const request = new ODataRequest('put', 'issues', params.issue.id, body);
			return this.makeODataRequest$<IssueResource>(request, 'api2').pipe(
				map((response) => {
					const mapper = new IssueResourceToIssueMapper();
					return mapper.map(response);
				})
			);
		}

		return of(null);
	}

	public delete(params: DeleteParams): Observable<void> {
		if (params?.issue) {
			const request = new ODataRequest('delete', 'issues', params.issue.id);
			return this.makeODataRequest$<IssueResource>(request, 'api2').pipe(
				map(() => null)
			);
		}
		return of(null);
	}
}

interface GetParams {
	searchTerm?: string;
	id?: string;
}

interface PostParams {
	issue: Issue;
}

interface PutParams {
	issue: Issue;
}

interface DeleteParams {
	issue: Issue;
}

